import type { List } from 'immutable';
import { Map } from 'immutable';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/DoneRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';
import UnlinkIcon from '@mui/icons-material/LinkOff';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import RepeatOneIcon from '@mui/icons-material/RepeatOneRounded';
import RepeatIcon from '@mui/icons-material/RepeatRounded';
import RuleIcon from '@mui/icons-material/Rule';
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded';
import ListItemIcon from '@mui/material/ListItemIcon';

import { useTheme } from '@mui/material/styles';

import { accountsSelectors } from 'app-components/flux/accounts/index';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { Goal } from 'app-components/flux/goals/goalsTypes';
import { goalsSelectors } from 'app-components/flux/goals';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import type { Tag } from 'app-components/flux/tags/tagsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';

import CreateReceiptMenuIcon from 'assets/icons/business/createReceiptMenu.inline.svg';
import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import { HandleDisconnectOption } from 'components/BillPresentment/utils';
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import { editReminderDialog } from 'components/Dialogs/EditReminderDialog/actions';
import { openInvestmentTransactionEditDialogAction } from 'components/Dialogs/InvestmentTransactionEditDialog';
import { showMatchTxnDialog } from 'components/Dialogs/MatchTxnDialog/actions';
import { DIALOG_TYPE_REFUND_EDIT } from 'components/Dialogs/RefundEditDialog';
import { TransactionRuleAction } from 'components/Dialogs/TransactionRules/actions';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import { InvoicePaymentUpsertAction } from 'components/InvoicePayment/actions';
import useQDialogs from 'components/QDialogs/useQDialogs';
import { showTxnDetailsDialog } from 'components/Transactions/DetailsDialog/actions';
import { useOpenInvoice } from 'containers/SalesAndBilling/invoiceHooks';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';

import { scheduledTransactionsSelectors, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import { tracker } from 'app-components/utils/core';
import { getLength, reduce } from 'app-components/utils/core/immutableHelpers';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';

import { allowSalesReceiptMatch, paidInvoiceExists } from 'app-components/flux/transactions/invoicesSelectors';
import { isManualTransaction, isRefundTxn, isUITransferTxn } from 'app-components/flux/transactions/transactionsUtils';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from '../Dialogs/ConfirmationDialog';
import type { InvoicePaymentFormProps } from '../InvoicePayment/types';
import { QButtonVariantEnum } from '../QButton';
import type { TxnOptionProps } from './types';
import { useInvoiceOptionsRaw } from './useInvoiceOptionsRaw';
import { showMatchSalesReceiptDialog } from 'components/ReceiptMatch/actions';

const useStyles = makeStyles()((theme) => ({
  hiddenIcon: {
    marginRight: 16,
    color: theme.palette.text.secondary,
    minWidth: '24px !important',
    opacity: 0,
  },
  highlightedIcon: {
    marginRight: 16,
    minWidth: '24px !important',
    color: theme.palette.primary.main,
  },
  imgClass: {
    marginLeft: 12,
    height: 24,
    width: 24,
    color: theme.palette.text.primary,
    '&.blue': {
      color: theme.palette.primary.main,
    },
  },
}));

type HookFunction<Props, ReturnType> = (props: Props) => ReturnType;

const createConditionalOptionsHook =
  <Props, ReturnType>(hook: HookFunction<Props, ReturnType>) =>
  (props: Props, isValid?: (props: Props) => boolean): ReturnType | [] => {
    if (!isValid || !isValid(props)) return []; // Validate using the provided function
    return hook(props); // Execute the hook if valid
  };

function getTransactionMenuMPProps(props: any): any {
  const { action, context, txn, ...otherProps } = props;
  const category = chartOfAccountsSelectors.getCoaStringSelector(null, txn?.coa, true);
  const type = txn.isBill
    ? 'BILL'
    : txn.isSubscription
      ? 'SUBSCRIPTION'
      : scheduledTransactionsUtils.typeFromCOA(txn?.coa ?? { id: '', type: 'NONE' });

  return {
    action: action,
    context,
    payee: txn?.payee,
    category,
    type: type,
    ...otherProps,
  };
}

export const useEditOptions = (props: TxnOptionProps) => {
  const { closeMenu, txnActionProps, txnActionCallback, context } = props;
  const { form, txnToEdit: txn, attributes } = txnActionProps;
  const { isReminder, isRefundIncompleteTxn } = attributes;
  const dispatch = useAppDispatch();

  const handleOpenInvoice = useOpenInvoice('transactions-menu');

  const handleOpenTxnDetails = (txnActionCallback: (() => void) | undefined) => {
    if (form === 'INVESTMENT_TRANSACTION_DETAILS') {
      dispatch(openInvestmentTransactionEditDialogAction(txn.toJS()));
    } else if (isRefundIncompleteTxn) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'refund-card',
            type: DIALOG_TYPE_REFUND_EDIT,
            allowNesting: true,
            props: Map({ transaction: txn }),
          }),
        ),
      );
    } else if (isReminder) {
      dispatch(editReminderDialog(txn, context));
    } else {
      dispatch(showTxnDetailsDialog({ txn, txnActionCallback, wrapperId: context }));
    }

    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'details', context, txn }));

    closeMenu();
  };

  const options = [];
  switch (form) {
    case 'INVOICE': {
      let title = 'Invoice';
      const label = (() => {
        switch (txn.subtype) {
          case TransactionSubType.Estimate:
            title = 'Estimate';
            return 'View estimate';
          case TransactionSubType.InvoiceCreditMemo:
            title = 'Credit Memo';
            return 'View credit memo';
          case TransactionSubType.SalesReceipt:
            title = 'Sales Receipt';
            return 'View sales receipt';
          case TransactionSubType.SalesRefund:
            title = 'Sales Refund';
            return 'View sales refund';
          case TransactionSubType.Invoice:
          default:
            return 'View invoice';
        }
      })();
      options.push({
        id: 'txn-menu-edit',
        label,
        onSelect: () => {
          handleOpenInvoice({
            invoiceId: txn.id ?? '',
            titleProp: title,
          });

          tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'invoice', context, txn }));

          closeMenu();
        },
        startIcon: <SubdirectoryArrowRightRoundedIcon />,
        divider: true,
      });
      break;
    }
    case 'INVESTMENT_TRANSACTION_DETAILS':
    case 'TRANSACTION_DETAILS': {
      let label = 'Edit transaction';
      if (isReminder) {
        label = 'Edit reminder';
      } else if (isRefundIncompleteTxn) {
        label = 'Edit refund';
      }
      options.push({
        id: 'txn-menu-edit',
        label,
        onSelect: () => handleOpenTxnDetails(txnActionCallback),
        startIcon: <EditIcon />,
        divider: true,
      });
      break;
    }
    case 'NONE':
    default:
      break;
  }

  return options;
};

export const usePaymentOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionProps } = props;
  const { attributes, txnToEdit: txn, txnOnOtherSide, matchingTransferIfTransfer, businessEnabled } = txnActionProps;
  const { isFutureDated } = attributes;
  const isPaidInvoiceExists = useAppSelector(paidInvoiceExists);
  const handleOpenInvoice = useOpenInvoice('transactions-menu');

  const incomeCOAs = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, txn.accountId));
  const businessSalesReceipts = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'businessSalesReceipts'),
  );
  const canBePaymentToAccountsReceivable = transactionsUtils.canBePaymentToAccountsReceivable(
    txn,
    account,
    incomeCOAs,
    matchingTransferIfTransfer,
  );
  const canBeRefundFromAccountsReceivable = transactionsUtils.canBeRefundFromAccountsReceivable(
    txn,
    account,
    matchingTransferIfTransfer,
  );

  const dispatch = useAppDispatch();

  const startInvoicePayment = (action: InvoicePaymentFormProps['action']) => () => {
    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'invoicePayment', context, txn }),
    );

    closeMenu();
    dispatch(
      InvoicePaymentUpsertAction({
        action,
        context,
        initIdType: 'PAYMENT',
        initId: txn.id,
        initScreen: 'MAIN',
      }),
    );
  };

  const options: any[] = [];
  if (attributes.isFutureDated || attributes.isReminder || !businessEnabled) {
    return options;
  }
  if (!attributes.isOtherSideInAccountsReceivable) {
    if (canBePaymentToAccountsReceivable) {
      options.push({
        id: 'txn-menu-record-as-payment',
        key: 'txn-menu-record-as-payment',
        startIcon: <PaymentsOutlinedIcon />,
        label: 'Apply payment',
        onSelect: startInvoicePayment('RECORD_PAYMENT'),
        divider: true,
      });
    }
    if (isPaidInvoiceExists && canBeRefundFromAccountsReceivable && !isFutureDated) {
      options.push({
        id: 'txn-menu-record-as-refund',
        key: 'txn-menu-record-as-refund',
        startIcon: <PaymentsOutlinedIcon />,
        label: 'Apply refund',
        onSelect: startInvoicePayment('RECORD_REFUND'),
        divider: true,
      });
    }
    if (businessSalesReceipts && allowSalesReceiptMatch(txn, incomeCOAs)) {
      options.push({
        id: 'txn-menu-convert-sales-receipt',
        key: 'txn-menu-convert-sales-receipt',
        startIcon: <CreateReceiptMenuIcon />,
        label: 'Create sales receipt',
        onSelect: () => {
          handleOpenInvoice({
            subtype: TransactionSubType.SalesReceipt,
            customerId: txn.customerId ?? undefined,
            projectId: txn.projectId ?? undefined,
            originalTxnId: txn?.id ?? undefined,
            startingPoint: 'Transaction',
          });
          closeMenu();
        },
        divider: true,
      });
    }
  }
  // if transfer txn
  if (txnOnOtherSide) {
    if (
      (attributes.isInAccountsReceivable && !transactionsUtils.isDebitTxn(txn)) ||
      (attributes.isOtherSideInAccountsReceivable &&
        txnOnOtherSide?.type !== 'INVOICE' &&
        transactionsUtils.isDebitTxn(txn))
    ) {
      options.push({
        id: 'txn-menu-edit-credit-allocations',
        key: 'txn-menu-edit-credit-allocations',
        startIcon: <PaymentsOutlinedIcon />,
        label: attributes.hasAllocations ? 'Edit payment' : 'Apply payment',
        onSelect: startInvoicePayment('EDIT_ALLOCATIONS'),
      });
    }
    if (
      (attributes.isInAccountsReceivable && transactionsUtils.isDebitTxn(txn)) ||
      (attributes.isOtherSideInAccountsReceivable &&
        txnOnOtherSide?.type !== 'INVOICE' &&
        !transactionsUtils.isDebitTxn(txn))
    ) {
      options.push({
        id: 'txn-menu-edit-debit-allocations',
        key: 'txn-menu-edit-debit-allocations',
        startIcon: <PaymentsOutlinedIcon />,
        label: attributes.hasAllocations ? 'Edit refund' : 'Apply refund',
        onSelect: startInvoicePayment('EDIT_ALLOCATIONS'),
      });
    }
  }

  return options;
};

export const useGoalOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionCallback, txnActionProps } = props;
  const { txnToEdit: txn } = txnActionProps;

  const goalOptionsFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'goalWithdrawTxns'));

  const { saveTransactions, deleteTransactions } = useTxnActions({
    wrapperId: context,
    callback: txnActionCallback,
  });

  const goals = useAppSelector(goalsSelectors.getResourcesById);
  const matchingGoal = goals.find((goal: Goal) => txn?.tags?.map((tag: any) => tag?.id).includes(goal.tagId));

  const matchingTxn = useAppSelector((state) =>
    transactionsSelectors.getGoalTxnsForIdAmountAndDate(state, {
      acctId: matchingGoal?.accountId,
      amount: txn.amount,
      date: txn.postedOn,
    }),
  ).first();

  const goalTagIds = goals.map((goal: Goal) => goal.tagId).toList();
  const isGoalTxn = txn?.tags?.some((tag: any) => goalTagIds.includes(tag?.id)) && txn?.coa?.type !== 'ACCOUNT';

  const unlinkFromGoal = () => {
    deleteTransactions([matchingTxn]);
    saveTransactions([
      txn.set(
        'tags',
        (txn?.tags as List<Tag> | undefined)?.filter((tag: Tag) => !goalTagIds.includes(tag.id)),
      ),
    ]);

    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'unlinkFromGoal', context, txn }),
    );

    setTimeout(() => {
      closeMenu();
    }, 1000);
  };

  const options: any[] = [];

  if (isGoalTxn && goalOptionsFF) {
    options.push({
      label: 'Goals',
      isSubheader: true,
    });
    options.push({
      label: 'Unlink from goal',
      onSelect: unlinkFromGoal,
      startIcon: <LinkOffRoundedIcon />,
    });
  }

  return options;
};

export const useExcludeOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionCallback, txnActionProps, hideExcludeReports, hideExcludeSpendingPlan } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;

  const { classes, cx } = useStyles();
  const theme = useTheme();

  const { saveTransactions } = useTxnActions({
    wrapperId: context,
    callback: txnActionCallback,
  });

  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, txn.accountId));

  const isInvestmentTxn = account?.type === 'INVESTMENT';
  const isInvoiceTxnType = txn.type === 'INVOICE';

  const disableExcludeReports =
    txn?.coa === null ? false : chartOfAccountsUtils.disableExcludeReports(txn?.coa, txn?.split);

  const options: any[] = [];
  if (
    attributes.isInAccountsReceivable ||
    attributes.isOtherSideInAccountsReceivable ||
    isInvestmentTxn ||
    isInvoiceTxnType
  ) {
    return options;
  }

  const doExcludeReports = () => {
    saveTransactions([txn.set('isExcludedFromReports', !txn.isExcludedFromReports)]);

    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'excludeReports', context, txn }),
    );

    // delay closing the menu to allow the user to see the checkmark toggling on or off
    setTimeout(() => {
      closeMenu();
    }, 1000);
  };
  const doExcludeF2S = () => {
    saveTransactions([txn.set('isExcludedFromF2S', !txn.isExcludedFromF2S)]);

    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'excludeSpendingPlan', context, txn }),
    );

    // delay closing the menu to allow the user to see the checkmark toggling on or off
    setTimeout(() => {
      closeMenu();
    }, 1000);
  };

  options.push({
    label: 'Exclude from:',
    isSubheader: true,
  });
  if (!hideExcludeSpendingPlan) {
    options.push({
      label: 'Spending Plan',
      onSelect: doExcludeF2S,
      labelColor: txn.isExcludedFromF2S ? theme.palette.primary.main : theme.palette.text.primary,
      startIcon: (
        <ListItemIcon
          classes={{
            root: txn.isExcludedFromF2S ? classes.highlightedIcon : classes.hiddenIcon,
          }}
        >
          <CheckIcon />
        </ListItemIcon>
      ),
      endIcon: <ExcludeFromSpendingPlan className={cx(classes.imgClass, txn.isExcludedFromF2S ? 'blue' : '')} />,
      divider: hideExcludeReports,
    });
  }
  if (!hideExcludeReports) {
    options.push({
      label: 'Reports',
      onSelect: doExcludeReports,
      labelColor: txn.isExcludedFromReports ? theme.palette.primary.main : theme.palette.text.primary,
      startIcon: (
        <ListItemIcon
          classes={{
            root: txn.isExcludedFromReports ? classes.highlightedIcon : classes.hiddenIcon,
          }}
        >
          <CheckIcon />
        </ListItemIcon>
      ),
      endIcon: <ExcludeFromReports className={cx(classes.imgClass, txn.isExcludedFromReports ? 'blue' : '')} />,
      disabled: disableExcludeReports,
      divider: true,
    });
  }

  return options;
};

export const useRecurringOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionCallback, txnActionProps } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const store = useAppStore();

  const dispatch = useAppDispatch();

  const { performTransactionAction, saveTransactions } = useTxnActions({
    callback: txnActionCallback,
    wrapperId: context,
  });

  const isIncome = categoriesSelectors.isIncomeCat(store.getState(), txn?.coa?.id ?? '');
  const account = accountsSelectors.getAccountById(store.getState(), txn.accountId);

  const isInvestmentTxn = account?.type === 'INVESTMENT';
  const isInvoiceTxnType = txn.type === 'INVOICE';
  const isPaymentTxnType = txn.type === 'PAYMENT';

  const isPersonalRefund = txn.subtype === TransactionSubType.Refund;

  // TODO: Should other side of payments to invoices be included in recurring options (can manually create one so why not?)

  const options: any[] = [];
  if (
    attributes.isInAccountsReceivable ||
    attributes.isOtherSideInAccountsReceivable ||
    isInvestmentTxn ||
    isInvoiceTxnType ||
    isPaymentTxnType ||
    attributes.isReminder
  ) {
    return options;
  }

  const toggleBill = () => {
    saveTransactions([txn.set('isBill', !txn.isBill)]);

    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'toggleBill', context, txn }));

    closeMenu();
  };

  const createRecurring = () => {
    dispatch(showTxnDetailsDialog({ txn, recurring: 'create', wrapperId: context }));

    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'createRecurring', context, txn }),
    );

    closeMenu();
  };

  const linkOrUnlinkRecurring = () => {
    if (txn.stModelId) {
      performTransactionAction({
        action: 'unlink-to-scheduled-txn',
        id: txn.id,
      });
    } else {
      dispatch(showTxnDetailsDialog({ txn, recurring: 'link', wrapperId: context }));
    }

    const action = txn.stModelId ? 'unlinkRecurring' : 'linkRecurring';
    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action, context, txn }));

    closeMenu();
  };

  const matchState = transactionsUtils.getMatchState(txn);

  const shouldShowOneTimeBill = !isIncome && !isPersonalRefund;
  const shouldShowNewRecurring = !isPersonalRefund;

  options.push({
    label: 'Series',
    isSubheader: true,
  });
  if (!txn?.stModelId) {
    if (shouldShowOneTimeBill) {
      options.push({
        label: 'Mark as one-time bill',
        onSelect: toggleBill,
        startIcon: txn.isBill ? <LinkOffRoundedIcon /> : <RepeatOneIcon />,
      });
    }
    if (shouldShowNewRecurring) {
      options.push({
        label: 'Create new series',
        onSelect: createRecurring,
        startIcon: <RepeatIcon />,
      });
    }
    // TODO: Validate that QCS can handle linking to recurring txn if txn is already matched
    options.push({
      label: 'Link to existing series',
      onSelect: linkOrUnlinkRecurring,
      startIcon: <LinkRoundedIcon />,
    });
  } else if (matchState !== 'matched') {
    options.push({
      label: 'Unlink series',
      onSelect: linkOrUnlinkRecurring,
      startIcon: <LinkRoundedIcon />,
    });
  }

  return options;
};

export const useDeleteOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionProps } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;

  const dispatch = useAppDispatch();

  const { deleteTransactions, performTransactionAction } = useTxnActions({
    wrapperId: context,
    robotMode: true,
  });
  const prevReminders = useAppSelector(
    (state) =>
      getLength(
        transactionsSelectors.getPreviousUnlinkedReminders(state, {
          stModelId: txn.stModelId,
          postedOn: txn.postedOn,
        }),
      ) > 0,
  );
  const categoryName = chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, true);

  const options: any[] = [];

  if (attributes.txnToEditWasFlipped) {
    return options;
  }

  let label;
  let content;
  let confirmLabel;
  switch (txn.subtype) {
    case TransactionSubType.Estimate:
      label = 'Delete estimate';
      content = 'Are you sure want to delete this estimate?';
      confirmLabel = label;
      break;
    case TransactionSubType.InvoiceCreditMemo:
      label = 'Delete credit memo';
      content =
        'Are you sure want to delete this credit memo? Any credits from this credit memo that has been allocated ';
      content += 'to debit transactions will be unallocated.';
      confirmLabel = label;
      break;
    case TransactionSubType.SalesReceipt:
      label = 'Delete sales receipt';
      content = 'Once deleted, your items and transaction data will be permanently removed and cannot be recovered.';
      confirmLabel = label;
      break;
    case TransactionSubType.SalesRefund:
      label = 'Delete sales refund';
      content = 'Are you sure want to delete this sales refund?';
      confirmLabel = label;
      break;
    case TransactionSubType.Refund:
      label = 'Delete refund';
      content = 'Are you sure want to delete this refund?';
      confirmLabel = label;
      break;
    case TransactionSubType.Invoice:
      label = txn.sentOn ? 'Delete invoice' : 'Delete draft invoice';
      content = txn.sentOn
        ? 'After deletion, associated payments will be removed from this invoice and still remain in your account. \n\nInvoiced items will be associated with the client as Ready to Bill.'
        : 'After deletion, all project items and expenses will remain linked to the client.';
      confirmLabel = 'Delete Invoice';
      break;
    default:
      break;
  }

  // Add delete option for unapplied payments
  if (attributes.isInAccountsReceivable && txn.type === 'CASH_FLOW') {
    // The same logic is used to show the "unapplied" status
    const allocationAmt = reduce(txn.allocations || [], 0, (acc, alloc) => acc + (alloc?.amount ?? 0));
    if (allocationAmt === 0) {
      label = 'Delete transaction';
      content = 'Are you sure you want to delete this item? This action cannot be undone.';
      confirmLabel = 'Delete Transaction';
    }
  }

  if (attributes.isReminder) {
    label = 'Dismiss reminder';
    content = `Are you sure you want to dismiss this reminder? \n\n${prevReminders ? 'This will also dismiss all earlier reminders in the series.' : ''} Future reminders won’t be affected. `;
    confirmLabel = label;
  }

  if (label && content && confirmLabel) {
    options.push({
      id: 'delete-invoice',
      label,
      labelColor: 'error',
      startIcon: <DeleteIcon sx={{ color: 'error.main' }} />,
      divider: true,
      onSelect: () => {
        tracker.track(
          tracker.events.transactionMenu,
          getTransactionMenuMPProps({ action: 'deleteInvoice', context, txn }),
        );
        closeMenu();
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'delete-txn-confirmation',
              type: DIALOG_CONFIRMATION,
              allowNesting: true,
              props: Map(
                mkConfirmationDialogProps({
                  title: label,
                  content,
                  confirmLabel,
                  confirmButtonVariant: QButtonVariantEnum.DELETE,
                  denyLabel: 'Cancel',
                  confirmAction: () => {
                    if (attributes.isReminder) {
                      performTransactionAction({
                        action: 'skip',
                        id: txn.id,
                      });
                      tracker.track(tracker.events.deleteReminder, {
                        source: window.location.pathname.split('/').filter(Boolean)[0] ?? 'dashboard',
                        category: categoryName,
                      });
                    } else {
                      deleteTransactions([txn]);
                    }
                  },
                  denyAction: () => dispatch(rootUiActions.removeDialog()),
                }),
              ),
            }),
          ),
        );
      },
    });
  }

  return options;
};

const useLinkToTransactionOptionsRaw = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionProps, txnActionCallback } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const { isPersonalRefund, isRefundIncompleteTxn, isReminder } = attributes;

  const { performTransactionAction } = useTxnActions({
    callback: txnActionCallback,
    wrapperId: context,
  });

  const dispatch = useAppDispatch();
  const matchState = transactionsUtils.getMatchState(txn);
  const categoryName = chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, true);

  const accept = () => {
    if (txn) {
      performTransactionAction({
        action: 'accept',
        id: txn.id,
      });
    }
    // tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'unMatch', context, txn }));
    closeMenu();
  };

  const startMatch = () => {
    if (txn.subtype === 'SALES_RECEIPT') {
      dispatch(
        showMatchSalesReceiptDialog({
          receipt: txn,
        }),
      );
    } else {
      const source =
        window.location.pathname === '/'
          ? 'dashboard'
          : window.location.pathname.replaceAll?.(/[/-]|(\d+)/g, (match) =>
              match === '/' ? '' : match === '-' ? ' ' : 'xxx',
            );
      dispatch(showMatchTxnDialog({ txn, wrapperId: context }));
      tracker.track(tracker.events.linkReminderToTxn, {
        source: source,
        category: categoryName,
      });
      if (isRefundIncompleteTxn) tracker.track(tracker.events.refundManualMatch, { location: 'upcoming' });
    }

    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'match', context, txn, subtype: txn.subtype }),
    );

    closeMenu();
  };

  const unMatch = () => {
    if (txn) {
      performTransactionAction({
        action: isPersonalRefund ? 'unmatch' : 'unlink-to-scheduled-txn',
        id: txn.id,
      });
    }

    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'unMatch', context, txn }));

    closeMenu();
  };

  const options: any[] = [];

  switch (matchState) {
    case 'manual':
      options.push({
        id: 'menu-link',
        label: 'Link to transaction',
        onSelect: startMatch,
        startIcon: <CompareArrowsIcon />,
        subtext: 'Match with your recent activity',
        divider: !isReminder,
      });
      break;
    case 'matched':
      options.push({
        id: 'menu-link',
        label: 'Unlink transaction',
        onSelect: unMatch,
        startIcon: <CompareArrowsIcon />,
        divider: true,
      });
      break;
    case 'downloaded':
      // no options
      break;
  }

  if (isReminder) {
    options.push({
      id: 'menu-link',
      label: 'Mark as paid',
      onSelect: accept,
      startIcon: <PlaylistAddCheckOutlinedIcon />,
      subtext: 'Add to transactions',
      divider: true,
    });
  }

  return options;
};

export const useLinkToTransactionOptions = createConditionalOptionsHook(useLinkToTransactionOptionsRaw);

export const useBillPresentmentOptions = (props: TxnOptionProps) => {
  const { closeMenu, txnActionProps, context } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const { isReminder } = attributes;

  const { dialogAlert } = useQDialogs();
  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, txn?.stModelId || ''),
  );
  const connectOption = useAppSelector((state) => billPresentmentSelectors.getShowConnectOption(state, model));

  const reconnect =
    model?.presentmentAccountId &&
    (model?.billerEnrollmentStatus === 'FAILED' || model?.billerEnrollmentStatus === 'DISCONNECTED');

  const options: any[] = [];
  const dispatch = useAppDispatch();

  if (!isReminder && !(connectOption || reconnect)) {
    return options;
  }
  const startConnect = () => {
    dispatch(doBillPresentment(model, location));
    closeMenu();
    tracker.track(reconnect ? tracker.events.fixBillerStart : tracker.events.addBillerStart, { initiator: location });
    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'connectBiller', context, txn }));
  };

  const unlink = () => {
    tracker.track(
      tracker.events.transactionMenu,
      getTransactionMenuMPProps({ action: 'disconnectBiller', context, txn }),
    );

    dialogAlert(
      'Disconnect biller',
      `Your ${model?.transaction?.payee}
        bill reminders will no longer be updated with the latest bill information.`,
      // @ts-expect-error FIXME TS7006
      (btnObj) => {
        if (btnObj.btnPressed === 'Disconnect') {
          HandleDisconnectOption(dispatch, model, location, true);
        }
      },
      ['Cancel', 'Disconnect'],
    );
  };

  if (connectOption || reconnect) {
    options.push({
      label: reconnect ? 'Reconnect biller' : 'Connect biller',
      labelColor: reconnect ? 'error' : 'text.primary',
      onSelect: startConnect,
      startIcon: reconnect ? <ErrorIcon color='error' /> : <LinkIcon />,
      id: 'menu-connect',
      divider: true,
    });
  }
  if (model?.presentmentAccountId) {
    options.push({
      startIcon: <UnlinkIcon />,
      label: 'Disconnect biller',
      id: 'menu-unlink',
      onSelect: unlink,
    });
  }
  return options;
};

export const useViewSeriesOptions = (props: TxnOptionProps) => {
  const { closeMenu, txnActionProps, context } = props;
  const { txnToEdit: txn } = txnActionProps;

  const dispatch = useAppDispatch();

  const options: any[] = [];

  if (!txn.stModelId) {
    return options;
  }

  const openView = () => {
    dispatch(showViewSeriesDialog(txn));

    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'viewSeries', context, txn }));

    closeMenu();
  };

  options.push({
    label: 'View Series',
    id: 'menu-view',
    onSelect: openView,
    startIcon: <OpenInBrowserIcon />,
    divider: true,
  });

  return options;
};

export const useInvoiceOptions = createConditionalOptionsHook(useInvoiceOptionsRaw);

export const useRuleOptions = (props: TxnOptionProps) => {
  const dispatch = useAppDispatch();
  const { closeMenu, txnActionProps, context } = props;
  const { txnToEdit: txn } = txnActionProps;

  const options: any[] = [];

  // Don't display this option if txn is: invoice, Balance Adjustment, Refund, Transfer, or manual Transaction
  if (txn.type === 'INVOICE' || isRefundTxn(txn) || isUITransferTxn(txn) || isManualTransaction(txn)) {
    return options;
  }

  const dialogId = 'add-memorized-rule';

  const handleClose = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
  };

  const handleOpenTxnRule = () => {
    dispatch(
      TransactionRuleAction({
        id: dialogId,
        transaction: txn,
        handleClose,
      }),
    );

    tracker.track(tracker.events.transactionMenu, getTransactionMenuMPProps({ action: 'createRule', context, txn }));

    closeMenu();
  };

  options.push({
    id: 'txn-menu-rule',
    label: 'Create a rule',
    onSelect: () => {
      handleOpenTxnRule();
      tracker.track(tracker.events.txnRuleStarted, { source: 'txnRegister' });
      closeMenu();
    },
    subtext: "Use this transaction's information to create a rule",
    startIcon: <RuleIcon />,
    divider: true,
  });

  return options;
};
